<script setup>
import { router, usePage } from '@inertiajs/vue3';
import { onMounted, reactive, ref, watch } from 'vue';
import JetButton from '@/Jetstream/Button.vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { QuillEditor, Delta } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';

// includes we need to front end locale magic
import { useI18n } from 'vue-i18n';

// Get the Programme, Organisation, and props we need to get the user preferred locale
const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const usePageProps = usePage().props;

// Get locale as set in cookie
const { locale, fallbackLocale} = useI18n({ useScope: 'global' });

// Set up all the data variables we are going to initially need
const props = defineProps({
    auditorFeedbackQuestion: Object,
    formData: Object,
    key: Number,
    datePicker: Object
});

const convertToObject = (json) => {
    if (json.constructor.name != 'Object') {
        json = JSON.parse(json)
    }
    return json;
}

const auditorFeedbackQuestion = props.auditorFeedbackQuestion;
const auditorFeedbackQuestionKey = auditorFeedbackQuestion.index_id;
const auditorFeedbackQuestionFieldType = auditorFeedbackQuestion.type;

// If the field type is 'select', set up appropriate variables and build the array of options
let selectListObject = '';
let selectListObjectKeys = [];
let selectList = [];
let selectOption = [];
let selectOptions = [];
if (auditorFeedbackQuestionFieldType == 'select') {
    selectListObject = props.auditorFeedbackQuestion.select_options;

    if (selectListObject.constructor.name != 'Object') {
        selectListObject = JSON.parse(selectListObject);
    }

    selectListObjectKeys = Object.keys(selectListObject);

    if ( selectListObjectKeys.includes(locale.value) ) {
        selectList = selectListObject[locale.value];
    }
    else {
        selectList = selectListObject[fallbackLocale.value];
    }

    const selectOptionNames = selectList.split(",");
    const selectOptionValues = selectList.split(",");

    for (var i = 0; i < selectOptionNames.length; i++) {
        selectOption = {};
        selectOption.name = selectOptionNames[i].trim();
        selectOption.value = selectOptionValues[i].trim();
        selectOptions.push(selectOption);
    }
}

const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
let date = ref(new Date());
if (auditorFeedbackQuestionFieldType == 'date') {
   date = props.formData[auditorFeedbackQuestionKey].answer;
}
const format = (date) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `Selected date is ${day}/${month}/${year}`;
}

const auditorWYSIWYGForm = reactive({
    auditorWYSIWYGField: '',
    auditorWYSIWYGDraftID: '',
    auditorWYSIWYGDraftLastSaved: '',
});

const auditorFeedbackQuestionLabel = auditorFeedbackQuestion.label;
const auditorFeedbackQuestionID = auditorFeedbackQuestion.id;
const auditorFeedbackQuestionUniqueID = 'auditorFeedbackQuestion-' + auditorFeedbackQuestionID;

if (auditorFeedbackQuestionFieldType == 'wysiwyg') {
    const auditorWYSIWYGEditor = ref('auditorWYSIWYGEditor');
}

onMounted(() => {});

const validation_error = reactive({
        comment: "",
 });

</script>

<template>

    <div class="mt-4">

        <label class="block mb-2" :for="auditorFeedbackQuestionUniqueID">
            <span v-if="convertToObject(props.auditorFeedbackQuestion.label)[$i18n.locale] || convertToObject(props.auditorFeedbackQuestion.label)[$i18n.fallbackLocale]" v-html="convertToObject(props.auditorFeedbackQuestion.label)[$i18n.locale] ? convertToObject(props.auditorFeedbackQuestion.label)[$i18n.locale] : convertToObject(props.auditorFeedbackQuestion.label)[$i18n.fallbackLocale]" class=""></span>
            <span v-if="auditorFeedbackQuestion.required == true" class="required-form-field">*</span>
        </label>

        <div v-if="auditorFeedbackQuestionFieldType == 'text'" class="form-control">
            <input
            v-model="formData[auditorFeedbackQuestionKey].answer"
            type="text"
            :name="auditorFeedbackQuestionID"
            :id="auditorFeedbackQuestionUniqueID"
            :required="auditorFeedbackQuestion.required == true"
            class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
        </div>

        <div v-else-if="auditorFeedbackQuestionFieldType == 'wysiwyg'" class="bg-white form-control">
            <QuillEditor
                v-model:content="formData[auditorFeedbackQuestionKey].answer"
                :name="auditorFeedbackQuestionID"
                :id="auditorFeedbackQuestionUniqueID"
                :required="auditorFeedbackQuestion.required == true"
                ref="auditorWYSIWYGEditor"
                ctheme="snow"
                contentType="html"
            />
        </div>

        <div v-else-if="auditorFeedbackQuestionFieldType == 'textarea'" class="form-control">
            <textarea
            v-model="formData[auditorFeedbackQuestionKey].answer"
            :name="auditorFeedbackQuestionID"
            :id="auditorFeedbackQuestionUniqueID"
            :required="auditorFeedbackQuestion.required == true"
            class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            ></textarea>
        </div>

        <div v-else-if="auditorFeedbackQuestionFieldType == 'select'" class="form-control">
            <select
            v-if="selectOptions"
            v-model="formData[auditorFeedbackQuestionKey].answer"
            :name="auditorFeedbackQuestionID"
            :id="auditorFeedbackQuestionUniqueID"
            :required="auditorFeedbackQuestion.required == true"
            class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            >
            <option value="">{{ $t('teams.questions.option') }}</option>
            <option v-for="optionItem in selectOptions"
                :name="optionItem.name"
                :value="optionItem.value"
            >{{optionItem.name}}</option>
            </select>
        </div>

        <div v-else-if="auditorFeedbackQuestionFieldType == 'checkbox'" class="form-control">
            <input
            type="checkbox"
            v-model="formData[auditorFeedbackQuestionKey].answer"
            :name="auditorFeedbackQuestionID"
            :id="auditorFeedbackQuestionUniqueID"
            :required="auditorFeedbackQuestion.required == true"
            class="mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
        </div>

        <div v-else-if="auditorFeedbackQuestionFieldType == 'date'" class="form-control">
            <div>
                <Datepicker
                v-model="formData[auditorFeedbackQuestionKey].answer"
                :format="format"
                :name="auditorFeedbackQuestionID"
                :id="auditorFeedbackQuestionUniqueID"
                :required="auditorFeedbackQuestion.required == true"
                />
            </div>

        </div>

    </div>

</template>
